import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import Navbar from "../components/Navbar";
import { useLanguage } from "../contexts/LanguageContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Wilayas les plus touchees par les incendies d'aout 2026
const AFFECTED_WILAYAS = [
  { code: "18", label_fr: "Jijel", label_ar: "جيجل" },
  { code: "06", label_fr: "Béjaïa", label_ar: "بجاية" },
  { code: "15", label_fr: "Tizi Ouzou", label_ar: "تيزي وزو" },
];

const IncendiesSoutien = () => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [doctorsByWilaya, setDoctorsByWilaya] = useState({});
  const [loading, setLoading] = useState(true);

  const EMERGENCY_NUMBERS = [
    { label: t("incendies_emergency_pc"), number: "14" },
    { label: t("incendies_emergency_samu"), number: "16" },
    { label: t("incendies_emergency_police"), number: "17" },
    { label: t("incendies_emergency_gendarmerie"), number: "1055" },
  ];

  useEffect(() => {
    const fetchAll = async () => {
      const results = {};
      await Promise.all(
        AFFECTED_WILAYAS.map(async (w) => {
          try {
            const res = await axios.get(`${API_URL}/api/doctors`, {
              params: { wilaya: w.code, limit: 6, page: 1 },
            });
            results[w.code] = res.data.doctors || [];
          } catch (err) {
            results[w.code] = [];
          }
        })
      );
      setDoctorsByWilaya(results);
      setLoading(false);
    };
    fetchAll();
  }, []);

  return (
    <>
      <Helmet>
        <title>{t("incendies_page_title")} | Tabbibak</title>
        <meta
          name="description"
          content="Tabbibak se mobilise avec les familles touchées par les incendies à Jijel, Béjaïa et Tizi-Ouzou. Numéros d'urgence, informations pratiques et médecins disponibles dans les wilayas touchées."
        />
        <link rel="canonical" href="https://tabbibak.com/incendies-2026" />
        <meta property="og:title" content={`${t("incendies_page_title")} | Tabbibak`} />
        <meta
          property="og:description"
          content="Numéros d'urgence, informations pratiques et médecins disponibles dans les wilayas touchées par les incendies."
        />
        <meta property="og:url" content="https://tabbibak.com/incendies-2026" />
      </Helmet>

      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />

        {/* Bandeau hero */}
        <div className="bg-[#1c1917] text-white">
          <div className="max-w-4xl mx-auto px-4 py-10 text-center">
            <h1 className="text-2xl md:text-3xl font-bold mb-3">
              {t("incendies_hero_title")}
            </h1>
            <p className="text-[#d6d3d1] max-w-2xl mx-auto leading-relaxed">
              {t("incendies_hero_text")}
            </p>
            <a
              href="https://www.ifrc.org/fr/national-societies-directory/croissant-rouge-algerien"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-6 bg-[#7f1d1d] hover:bg-[#991b1b] transition-colors text-white font-semibold px-6 py-3 rounded-lg"
            >
              {t("incendies_donate_btn")}
            </a>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-10">
          {/* Numeros d'urgence */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-[#1A2B3C] mb-4">
              {t("incendies_emergency_title")}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {EMERGENCY_NUMBERS.map((e) => (
                <a
                  key={e.number}
                  href={`tel:${e.number}`}
                  className="bg-white rounded-xl border border-[#E2E8F0] p-4 text-center hover:shadow-md transition-shadow"
                >
                  <div className="text-2xl font-bold text-[#0596DE]">{e.number}</div>
                  <div className="text-sm text-[#64748B] mt-1">{e.label}</div>
                </a>
              ))}
            </div>
          </section>

          {/* Conseils de securite */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-[#1A2B3C] mb-4">
              {t("incendies_safety_title")}
            </h2>

            <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-xl p-5 mb-4">
              <h3 className="font-semibold text-[#7f1d1d] mb-2">
                {t("incendies_safety_alert_title")}
              </h3>
              <p className="text-sm text-[#64748B] leading-relaxed">
                {t("incendies_safety_alert_text")}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-[#E2E8F0] p-5">
                <h3 className="font-semibold text-[#1A2B3C] mb-3">
                  {t("incendies_before_title")}
                </h3>
                <ul className="text-sm text-[#64748B] space-y-2 list-disc list-inside">
                  <li>{t("incendies_before_1")}</li>
                  <li>{t("incendies_before_2")}</li>
                  <li>{t("incendies_before_3")}</li>
                  <li>{t("incendies_before_4")}</li>
                  <li>{t("incendies_before_5")}</li>
                </ul>
              </div>

              <div className="bg-white rounded-xl border border-[#E2E8F0] p-5">
                <h3 className="font-semibold text-[#1A2B3C] mb-3">
                  {t("incendies_during_title")}
                </h3>
                <ul className="text-sm text-[#64748B] space-y-2 list-disc list-inside">
                  <li>{t("incendies_during_1")}</li>
                  <li>{t("incendies_during_2")}</li>
                  <li>{t("incendies_during_3")}</li>
                  <li>{t("incendies_during_4")}</li>
                  <li>{t("incendies_during_5")}</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Medecins par wilaya touchee */}
          <section>
            <h2 className="text-xl font-bold text-[#1A2B3C] mb-2">
              {t("incendies_doctors_title")}
            </h2>
            <p className="text-[#64748B] mb-6">
              {t("incendies_doctors_subtitle")}
            </p>

            {AFFECTED_WILAYAS.map((w) => {
              const wilayaLabel = lang === "ar" ? w.label_ar : w.label_fr;
              return (
                <div key={w.code} className="mb-8">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-[#1A2B3C]">{wilayaLabel}</h3>
                    <button
                      onClick={() => navigate(`/search?wilaya=${w.code}`)}
                      className="text-[#0596DE] text-sm font-medium hover:underline"
                    >
                      {t("incendies_see_all")}
                    </button>
                  </div>

                  {loading ? (
                    <div className="grid md:grid-cols-2 gap-4">
                      {[1, 2].map((i) => (
                        <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
                          <div className="h-5 bg-gray-200 rounded w-1/2 mb-2" />
                          <div className="h-4 bg-gray-200 rounded w-1/3" />
                        </div>
                      ))}
                    </div>
                  ) : (doctorsByWilaya[w.code] || []).length === 0 ? (
                    <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 text-center text-[#64748B]">
                      {t("incendies_no_doctors")} {wilayaLabel}.
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {doctorsByWilaya[w.code].map((doctor) => (
                        <div
                          key={doctor.doctor_id}
                          className="bg-white rounded-xl border border-[#E2E8F0] p-4 flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => navigate(`/doctor/${doctor.doctor_id}`)}
                        >
                          <div className="w-11 h-11 rounded-lg bg-[#E6F4FB] flex items-center justify-center text-[#0596DE] font-bold flex-shrink-0">
                            {doctor.name?.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-[#1A2B3C] truncate">{doctor.name}</div>
                            <div className="text-[#0596DE] text-sm truncate">{doctor.specialty}</div>
                            <div className="text-[#64748B] text-xs truncate">{doctor.address}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        </div>
      </div>
    </>
  );
};

export default IncendiesSoutien;
